import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ThemeProvider, Tooltip, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import muiThemeNew from 'config/muiThemeNew';
import { useDownloadManager, type DownloadJob, type DownloadPhase } from 'components/Complaints/Complaint/EvidenceV2/download/DownloadManagerContext';
import { Masked } from 'components/common/masked';

/**
 * Global, route-independent progress UI for evidence-bundle downloads.
 *
 * Rendered once at app root (inside {@link DownloadManagerProvider}); persists
 * across navigation so a bundle keeps building while the user moves around the
 * app. This is the "Option A — Calm" download toaster from the design handoff:
 * a quiet white card docked bottom-right, brand red used only as a thin progress
 * accent, with a per-download row that walks through gather → compress → finalise
 * step messages and a perceived-progress bar (the lambda zips server-side, so
 * there's no real byte progress — the % is a calm, honest fake that always
 * resolves to 100% the moment the ZIP is READY).
 */

/** How long a completed (READY) card lingers before auto-dismissing. */
const READY_AUTODISMISS_MS = 6000;

/**
 * Calm palette from the Evidence design system. Red is the live app brand
 * (`#DC2040`) used sparingly as an accent; everything else is soft neutrals.
 */
const PALETTE = {
  ink: '#1a1a1a',
  ink2: '#4a4a4a',
  mute: '#8a8a8e',
  line: '#e7e7ea',
  surface: '#f6f6f7',
  red: '#DC2040',
  redTint: '#fdf2f3',
  green: '#1f7a4d',
  greenTint: '#e8f3ec',
};

// Card entrance + chip pop. Injected once; harmless in jsdom (animations no-op).
const CSS_ID = 'dl-toaster-css';
if (typeof document !== 'undefined' && !document.getElementById(CSS_ID)) {
  const el = document.createElement('style');
  el.id = CSS_ID;
  el.textContent = `
  @keyframes dlToasterRise { from { transform: translateY(10px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
  @keyframes dlToasterPop { 0% { transform: scale(.6) } 60% { transform: scale(1.08) } 100% { transform: scale(1) } }
  @keyframes dlToasterSpin { to { transform: rotate(360deg) } }
  @keyframes dlToasterIndet { 0% { left: -40% } 100% { left: 100% } }`;
  document.head.appendChild(el);
}

const isActive = (phase: DownloadPhase): boolean => phase === 'creating' || phase === 'pending' || phase === 'processing';

/**
 * "What the lambda is doing" stages, shown one at a time while the bundle builds.
 *
 * There's no real progress to show — the lambda zips server-side and the browser
 * gets a single ZIP, so we never know a byte %. Rather than fake a bar (which
 * flickers when the build finishes just after it appears), we keep one calm
 * indeterminate sweep the whole time and walk these stages so it always reads as
 * alive. Each stage carries its OWN dwell time (`ms`), weighted to feel like the
 * real work — collecting/compressing linger, the lighter steps pass quicker — so
 * it never flicks through them. Once the scripted stages are done we loop the
 * REASSURE lines: the "still working" promise the user wants on long/GB bundles.
 */
const WORKING_STAGES: Array<{ text: string; ms: number }> = [
  { text: 'Gathering your files…', ms: 3000 },
  { text: 'Collecting your documents…', ms: 7000 },
  { text: 'Scanning attachments…', ms: 4500 },
  { text: 'Compressing into a ZIP…', ms: 8000 },
  { text: 'Packaging your bundle…', ms: 4500 },
  { text: 'Finalising your download…', ms: 6000 },
];
const REASSURE_STAGES: Array<{ text: string; ms: number }> = [
  { text: 'Large bundle — still working…', ms: 7000 },
  { text: 'Still working on it…', ms: 7000 },
];

/** The stage (text + how long to hold it) shown at a given step index. */
const stageAt = (step: number): { text: string; ms: number } =>
  step < WORKING_STAGES.length ? WORKING_STAGES[step] : REASSURE_STAGES[(step - WORKING_STAGES.length) % REASSURE_STAGES.length];

// ── Small presentational pieces ──────────────────────────────────────────────

/** Indeterminate sweep — a red segment that glides across while we work. */
const IndetBar: React.FC = () => (
  <Box sx={{ position: 'relative', height: 4, borderRadius: 999, bgcolor: '#ededef', overflow: 'hidden' }}>
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: '40%',
        borderRadius: 999,
        bgcolor: PALETTE.red,
        animation: 'dlToasterIndet 1.15s ease-in-out infinite',
      }}
    />
  </Box>
);

/** Spinning ring for the gathering leader chip. */
const Spinner: React.FC<{ size?: number; color: string }> = ({ size = 16, color }) => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: size, height: size, animation: 'dlToasterSpin .9s linear infinite' }}>
    <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeOpacity="0.22" strokeWidth="2.2" />
    <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
  </Box>
);

/** 30×30 rounded leader chip carrying the per-phase status icon. */
const LeaderChip: React.FC<{ bg: string; fg: string; children: React.ReactNode }> = ({ bg, fg, children }) => (
  <Box
    sx={{
      width: 30,
      height: 30,
      borderRadius: '9px',
      flexShrink: 0,
      bgcolor: bg,
      color: fg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'dlToasterPop .3s ease both',
    }}
  >
    {children}
  </Box>
);

const leaderFor = (phase: DownloadPhase): React.ReactNode => {
  const ICON = { fontSize: 18 } as const;
  switch (phase) {
    case 'ready':
      return (
        <LeaderChip bg={PALETTE.greenTint} fg={PALETTE.green}>
          <CheckRoundedIcon sx={ICON} />
        </LeaderChip>
      );
    case 'failed':
      return (
        <LeaderChip bg={PALETTE.redTint} fg={PALETTE.red}>
          <ErrorOutlineRoundedIcon sx={ICON} />
        </LeaderChip>
      );
    case 'expired':
      return (
        <LeaderChip bg={PALETTE.surface} fg={PALETTE.mute}>
          <AccessTimeRoundedIcon sx={ICON} />
        </LeaderChip>
      );
    case 'cancelled':
      return (
        <LeaderChip bg={PALETTE.surface} fg={PALETTE.mute}>
          <CloseRoundedIcon sx={ICON} />
        </LeaderChip>
      );
    default:
      return (
        <LeaderChip bg={PALETTE.surface} fg={PALETTE.ink}>
          <DownloadRoundedIcon sx={ICON} />
        </LeaderChip>
      );
  }
};

/** Split "Evidence bundle (4 files)" into a bold name + a muted "(…files)". */
const splitLabel = (label: string): { name: string; suffix: string | null } => {
  const m = label.match(/^(.*?)\s*(\([^)]*\))\s*$/);
  return m ? { name: m[1], suffix: m[2] } : { name: label, suffix: null };
};

// ── Per-download row ─────────────────────────────────────────────────────────

interface DownloadRowProps {
  job: DownloadJob;
  withDivider: boolean;
  onDismiss: (id: string) => void;
  onRetry: (id: string) => void;
  onCancel: (id: string) => void;
}

const DownloadRow: React.FC<DownloadRowProps> = ({ job, withDivider, onDismiss, onRetry, onCancel }) => {
  const active = isActive(job.phase);
  // Only 'processing' means a worker is genuinely building the ZIP. While
  // 'creating'/'pending' the bundle is just requested/queued — we must NOT walk
  // the "Gathering / Compressing / Packaging" stages then, as that would claim
  // work that hasn't started.
  const working = job.phase === 'processing';
  const [step, setStep] = useState(0);

  // Walk the working stages while the bundle builds. There's no real % to show
  // (server-side zip), so this is the only "we're alive" signal besides the
  // indeterminate sweep. Each stage holds for its own dwell time so it never
  // flicks; we self-schedule the next stage rather than use a fixed interval.
  // Only runs once the server reports PROCESSING; resets on each fresh build.
  useEffect(() => {
    if (!working) {
      setStep(0);
      return undefined;
    }
    setStep(0);
    let current = 0;
    let timer: ReturnType<typeof setTimeout>;
    const advance = () => {
      timer = setTimeout(() => {
        current += 1;
        setStep(current);
        advance();
      }, stageAt(current).ms);
    };
    advance();
    return () => clearTimeout(timer);
  }, [working, job.id]);

  // Completed downloads tidy themselves away after a short pause.
  useEffect(() => {
    if (job.phase !== 'ready') return undefined;
    const t = setTimeout(() => onDismiss(job.id), READY_AUTODISMISS_MS);
    return () => clearTimeout(t);
  }, [job.phase, job.id, onDismiss]);

  const { name, suffix } = splitLabel(job.label);
  const sub = ((): { text: string; tone: string } => {
    switch (job.phase) {
      case 'ready':
        return { text: 'Download ready — saved to your device', tone: PALETTE.mute };
      case 'failed':
        return { text: job.error ?? 'Couldn’t prepare the bundle', tone: PALETTE.red };
      case 'expired':
        return { text: 'Download link expired', tone: PALETTE.mute };
      case 'cancelled':
        return { text: 'Download cancelled', tone: PALETTE.mute };
      default:
        // 'creating' / 'pending' → still queued, no worker yet: stay honest.
        // 'processing' → a worker is building, so walk the working stages.
        return { text: working ? stageAt(step).text : 'Waiting to start…', tone: PALETTE.mute };
    }
  })();

  const leader = active ? (
    <LeaderChip bg={PALETTE.surface} fg={PALETTE.ink2}>
      <Spinner size={16} color={PALETTE.ink2} />
    </LeaderChip>
  ) : (
    leaderFor(job.phase)
  );

  return (
    <Box sx={{ px: '16px', py: '13px', borderTop: withDivider ? `1px solid ${PALETTE.line}` : 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {leader}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/*
            job.label is a `<caseReference>.zip` filename — PII — rendered as
            both visible text and the `title` attribute. Masked as a single
            node via the polymorphic `Masked` primitive (`as={Typography}`)
            rather than a dedicated semantic component, since the value is a
            filename, not a bare case reference (CD-8245, Task 2.9).
          */}
          <Masked as={Typography} noWrap title={job.label} sx={{ fontSize: 14, fontWeight: 600, color: PALETTE.ink, lineHeight: 1.3 }}>
            {name}
            {suffix && (
              <Box component="span" sx={{ color: PALETTE.mute, fontWeight: 500 }}>
                {' '}
                {suffix}
              </Box>
            )}
          </Masked>
          <Typography noWrap sx={{ fontSize: 12.5, color: sub.tone, mt: '2px' }}>
            {sub.text}
          </Typography>
        </Box>

        {active ? (
          <Tooltip title="Cancel download">
            <IconButton
              size="small"
              aria-label={`Cancel ${job.label}`}
              onClick={() => onCancel(job.id)}
              sx={{ width: 26, height: 26, color: PALETTE.mute, flexShrink: 0, '&:hover': { bgcolor: PALETTE.redTint, color: PALETTE.red } }}
            >
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Dismiss">
            <IconButton
              size="small"
              aria-label={`Dismiss ${job.label}`}
              onClick={() => onDismiss(job.id)}
              sx={{ width: 26, height: 26, color: PALETTE.mute, flexShrink: 0, '&:hover': { bgcolor: PALETTE.surface } }}
            >
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {active && (
        <Box sx={{ mt: '12px' }}>
          <IndetBar />
        </Box>
      )}

      {(job.phase === 'failed' || job.phase === 'expired') && (
        <RowLink color={PALETTE.red} icon={<RefreshRoundedIcon sx={{ fontSize: 14 }} />} label="Try again" onClick={() => onRetry(job.id)} />
      )}
      {job.phase === 'cancelled' && (
        <RowLink color={PALETTE.ink} icon={<DownloadRoundedIcon sx={{ fontSize: 14 }} />} label="Download again" onClick={() => onRetry(job.id)} />
      )}
    </Box>
  );
};

/** Quiet text-link action ("Try again" / "Download again"). */
const RowLink: React.FC<{ color: string; icon: React.ReactNode; label: string; onClick: () => void }> = ({ color, icon, label, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      mt: '12px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      p: 0,
      font: 'inherit',
      fontSize: 12.5,
      fontWeight: 600,
      color,
      '&:hover': { textDecoration: 'underline' },
    }}
  >
    {icon}
    {label}
  </Box>
);

// ── Panel summary helpers ────────────────────────────────────────────────────

const plural = (n: number, word: string): string => `${n} ${word}${n > 1 ? 's' : ''}`;

const summaryLine = (jobs: DownloadJob[]): string => {
  const active = jobs.filter((j) => isActive(j.phase)).length;
  if (active > 0) return `${plural(active, 'download')} in progress`;

  const failed = jobs.filter((j) => j.phase === 'failed' || j.phase === 'expired').length;
  const cancelled = jobs.filter((j) => j.phase === 'cancelled').length;
  const ready = jobs.filter((j) => j.phase === 'ready').length;

  if (ready && !failed && !cancelled) return `${plural(ready, 'download')} complete`;
  if (failed && !ready && !cancelled) return `${plural(failed, 'download')} failed`;
  if (cancelled && !ready && !failed) return cancelled > 1 ? `${cancelled} downloads cancelled` : 'Download cancelled';
  return plural(jobs.length, 'download');
};

const dotColor = (jobs: DownloadJob[]): string => {
  if (jobs.some((j) => isActive(j.phase))) return PALETTE.red;
  if (jobs.some((j) => j.phase === 'failed' || j.phase === 'expired')) return PALETTE.red;
  if (jobs.some((j) => j.phase === 'ready')) return PALETTE.green;
  return PALETTE.mute;
};

// ── Widget ───────────────────────────────────────────────────────────────────

const DownloadManagerWidget: React.FC = () => {
  const { jobs, dismiss, retry, cancel } = useDownloadManager();
  const [collapsed, setCollapsed] = useState(false);
  // Cancelling an in-flight download asks for confirmation first (the bundle is
  // still being prepared; cancelling means starting over).
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  if (jobs.length === 0) return null;

  const anyActive = jobs.some((j) => isActive(j.phase));

  return (
    <ThemeProvider theme={muiThemeNew}>
      <Box
        sx={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1400, width: 380, maxWidth: 'calc(100vw - 48px)' }}
        role="status"
        aria-live="polite"
      >
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            border: `1px solid ${PALETTE.line}`,
            boxShadow: '0 12px 32px rgba(20,20,30,.16), 0 2px 6px rgba(20,20,30,.08)',
            overflow: 'hidden',
            animation: 'dlToasterRise .34s cubic-bezier(.2,.7,.3,1) both',
          }}
        >
          {/* header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 12px 12px 16px',
              borderBottom: collapsed ? 'none' : `1px solid ${PALETTE.line}`,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: PALETTE.ink, letterSpacing: '-.01em' }}>Downloads</Typography>
              <Typography sx={{ fontSize: 11.5, color: PALETTE.mute, mt: '1px' }}>{summaryLine(jobs)}</Typography>
            </Box>

            {collapsed && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  flexShrink: 0,
                  bgcolor: dotColor(jobs),
                  boxShadow: anyActive ? `0 0 0 3px ${PALETTE.red}22` : 'none',
                }}
              />
            )}

            <Tooltip title={collapsed ? 'Show downloads' : 'Minimise'}>
              <IconButton
                size="small"
                aria-label={collapsed ? 'Show downloads' : 'Minimise downloads'}
                onClick={() => setCollapsed((c) => !c)}
                sx={{ width: 28, height: 28, color: PALETTE.mute, '&:hover': { bgcolor: PALETTE.surface } }}
              >
                <ExpandMoreRoundedIcon sx={{ fontSize: 18, transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* rows */}
          {!collapsed && (
            <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {jobs.map((job, i) => (
                <DownloadRow
                  key={job.id}
                  job={job}
                  withDivider={i > 0}
                  onDismiss={dismiss}
                  onRetry={retry}
                  onCancel={(id) => setConfirmCancelId(id)}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={confirmCancelId !== null} onClose={() => setConfirmCancelId(null)} aria-labelledby="dl-cancel-title">
        <DialogTitle id="dl-cancel-title" sx={{ fontWeight: 700 }}>
          Cancel this download?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: PALETTE.ink2 }}>
            Your evidence bundle is still being prepared. If you cancel now you’ll need to start the download again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setConfirmCancelId(null)} sx={{ textTransform: 'none', color: PALETTE.ink2 }}>
            Keep downloading
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (confirmCancelId) cancel(confirmCancelId);
              setConfirmCancelId(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Cancel download
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default DownloadManagerWidget;
